import React, { useState, useEffect, useCallback } from 'react';
import styles from './GameArea.module.css';
import {
  createBoard,
  randomTetromino,
  checkCollision,
  rotateTetromino,
  mergeTetromino,
  clearLines,
  normalizeTetrominoPreview
} from '../../../utils/gameUtils';
import { findBestMove } from '../../../utils/aiController';
import Guide from '../../TournamentPages/Guide/Guide';

const GameArea = () => {
  const [board, setBoard] = useState(createBoard());
  const [tetromino, setTetromino] = useState(randomTetromino());
  const [score, setScore] = useState(0);
  const [lines, setLines] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [nextTetromino, setNextTetromino] = useState(randomTetromino());
  const isAIEnabled = true; 
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);

  const moveBlock = useCallback((dx, dy) => {
    const newPos = { x: tetromino.pos.x + dx, y: tetromino.pos.y + dy };
    if (!checkCollision(board, tetromino, newPos)) {
      setTetromino(prev => ({ ...prev, pos: newPos }));
      return true;
    }
    return false;
  }, [board, tetromino]);

  const rotatePiece = useCallback(() => {
    const rotated = { ...tetromino, matrix: rotateTetromino(tetromino.matrix) };
    if (!checkCollision(board, rotated, tetromino.pos)) {
      setTetromino(rotated);
    }
  }, [board, tetromino]);

  const handleKeyPress = useCallback((event) => {
    if (gameOver || isPaused) return;

    switch (event.key) {
      case 'ArrowLeft':
        moveBlock(-1, 0);
        break;
      case 'ArrowRight':
        moveBlock(1, 0);
        break;
      case 'ArrowDown':
        moveBlock(0, 1);
        break;
      case 'ArrowUp':
        rotatePiece();
        break;
      case ' ':
        setIsPaused(prev => !prev);
        break;
      default:
        break;
    }
  }, [gameOver, isPaused, moveBlock, rotatePiece]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  useEffect(() => {
    if (gameOver || isPaused) return;

    const gameLoop = setInterval(() => {
      if (!moveBlock(0, 1)) {
        const newBoard = mergeTetromino(board, tetromino);
        const { newBoard: clearedBoard, lines: clearedLines } = clearLines(newBoard);
        
        setBoard(clearedBoard);
        setLines(prev => prev + clearedLines);
        setScore(prev => prev + (clearedLines * 100));
        
        setTetromino(nextTetromino);
        setNextTetromino(randomTetromino());

        if (checkCollision(clearedBoard, nextTetromino, { x: 3, y: 0 })) {
          setGameOver(true);
        }
      }
    }, 300);

    return () => {
      clearInterval(gameLoop);
    };
  }, [board, tetromino, nextTetromino, gameOver, isPaused, moveBlock]);

  useEffect(() => {
    if (!isAIEnabled || gameOver || isPaused) return;

    let aiTimeout;
    let lastMove = { x: tetromino.pos.x, rotation: 0 };
    
    const executeAIMove = () => {
      try {
        const bestMove = findBestMove(board, tetromino);

        if (bestMove.x !== lastMove.x || bestMove.rotation !== lastMove.rotation) {
          while (lastMove.rotation < bestMove.rotation) {
            rotatePiece();
            lastMove.rotation++;
          }

          const moveX = bestMove.x - tetromino.pos.x;
          if (moveX < 0) {
            moveBlock(-1, 0);
          } else if (moveX > 0) {
            moveBlock(1, 0);
          }
          
          lastMove = { x: tetromino.pos.x, rotation: bestMove.rotation };
        }
        
        aiTimeout = setTimeout(executeAIMove, 50);
        
      } catch (error) {
      }
    };
    aiTimeout = setTimeout(executeAIMove, 50);

    return () => {
      if (aiTimeout) {
        clearTimeout(aiTimeout);
      }
    };
  }, [isAIEnabled, board, tetromino, gameOver, isPaused, moveBlock, rotatePiece]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowOverlay(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={styles.gameArea}>
      <div className={`${styles.glassOverlay} ${showOverlay ? styles.show : ''}`}>
        <div className={styles.airdropContainer}>
          <div className={styles.airdropTitle}>
            Tetris Tournament 2025 Season One - MINO Airdrop Mission is now live! Earn your $MINO ($50 worth) tokens by reffering!
          </div>
          <button 
            className={styles.airdropButton} 
            onClick={() => setIsGuideOpen(true)}
          >
            Start Now
          </button>
        </div>
      </div>
      <div className={styles.gameHeader}>
        <div className={styles.nextBlock}>
          <div className={styles.nextBlockTitle}>
            <span>NEXT TETROMINO</span>
          </div>
          <div className={styles.previewContainer}>
            <div className={styles.preview}>
              {normalizeTetrominoPreview(nextTetromino.matrix).map((row, y) => (
                <div key={y} className={styles.row}>
                  {row.map((cell, x) => (
                    <div
                      key={`${y}-${x}`}
                      className={styles.cell}
                      style={{
                        backgroundColor: cell ? nextTetromino.color : 'transparent'
                      }}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className={styles.stats}>
          <span>SCORE: {score}</span>
          <span>Burned $MINO: {lines}</span>
          <span>Market Cap: 0</span>
        </div>
      </div>
      <div className={styles.gameContainer}>
        <div className={styles.board}>
          {board.map((row, y) => (
            <div key={y} className={styles.row}>
              {row.map((cell, x) => (
                <div
                  key={`${y}-${x}`}
                  className={styles.cell}
                  style={{
                    backgroundColor:
                      tetromino.matrix[y - tetromino.pos.y]?.[x - tetromino.pos.x] &&
                      y >= tetromino.pos.y &&
                      x >= tetromino.pos.x
                        ? tetromino.color
                        : cell || 'transparent'
                  }}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
      <Guide 
        isOpen={isGuideOpen} 
        onClose={() => setIsGuideOpen(false)} 
      />
    </div>
  );
};

export default GameArea;